import { useEffect, useSyncExternalStore } from 'react';
import { useSettings } from '@/lib/store';

/* Theme is applied from React state, and mirrored into localStorage so the
   inline script in index.html can apply it before first paint (no flash).
   Device-local on purpose: a phone in sepia shouldn't force the laptop into it. */
export function useApplyTheme() {
  const theme = useSettings((s) => s.settings.theme);
  const glass = useSettings((s) => s.settings.glass);
  useEffect(() => {
    const mq = matchMedia('(prefers-color-scheme: light)');
    const apply = () => {
      const t = theme === 'system' ? (mq.matches ? 'light' : 'dark') : theme;
      document.documentElement.setAttribute('data-theme', t);
      const bg = getComputedStyle(document.documentElement).getPropertyValue('--bg').trim();
      document.querySelectorAll('meta[name="theme-color"]').forEach((m) => m.setAttribute('content', bg || '#0b0f0d'));
    };
    apply();
    try { localStorage.setItem('ftu_theme', theme); } catch {}
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, [theme]);
  const accent = useSettings((s) => s.settings.accent);
  useEffect(() => {
    if (accent && accent !== 'emerald') document.documentElement.setAttribute('data-accent', accent);
    else document.documentElement.removeAttribute('data-accent');
    try { localStorage.setItem('ftu_accent', accent ?? 'emerald'); } catch {}
  }, [accent]);
  useEffect(() => {
    document.documentElement.style.setProperty('--glass-level', String(glass / 100));
    try { localStorage.setItem('ftu_glass', String(glass)); } catch {}
  }, [glass]);
}

export type Resolved = 'dark' | 'light' | 'sepia';

const lightMq = () => matchMedia('(prefers-color-scheme: light)');
const subscribeMq = (cb: () => void) => { const m = lightMq(); m.addEventListener('change', cb); return () => m.removeEventListener('change', cb); };

/* Derived from settings (not read back from the DOM): components that pick a
   theme-specific font must re-render in the same pass the theme changes, not
   one effect later — otherwise the tajweed page keeps the previous theme's font. */
export function useResolvedTheme(): Resolved {
  const theme = useSettings((s) => s.settings.theme);
  const prefersLight = useSyncExternalStore(subscribeMq, () => lightMq().matches, () => false);
  return theme === 'system' ? (prefersLight ? 'light' : 'dark') : theme;
}
